import React from "react";
import { connect } from "react-redux";

import File from "../core/types/files/File";
import Utility from "../core/foundation/Utility";
import { SystemFileType } from "../core/types/enums/SystemFileType";
import MainToolbarContents from "../core/components/toolbar/MainToolbarContents";
import ToolbarButton from "../core/components/toolbar/ToolbarButton";
import { AppDispatch, IAppState } from "../core/state/Store";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import ButtonMenu from "../core/components/buttons/ButtonMenu";
import Button from "../core/components/buttons/Button";
import RadioButton from "../core/components/inputform/RadioButton";
import InputGroup from "../core/components/group/InputGroup";
import { addAlert } from "../core/state/system/Alerts";
import AlertEntry, {
    AlertResult,
    AlertTypes,
} from "../core/types/system/AlertEntry";
import {
    addFolder,
    copyFiles,
    deleteFiles,
    downloadFile,
    FileRename,
    getFolderContents,
    initialiseFolders,
    moveFiles,
    renameFiles,
    uploadFile,
} from "../core/state/files/Folders";
import Folder from "../core/types/files/Folder";
import Group from "../core/types/archive/Group";
import { addFileExplorer } from "../core/state/system/FileExplorers";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import BrowserFile from "../core/types/files/BrowserFile";
import { ContextMenuContextType } from "../core/components/contextmenu/ContextMenuContext";
import ContextFragment from "../core/components/contextmenu/ContextFragment";
import ContextMenuItem from "../core/components/contextmenu/ContextMenuItem";
import Campus from "../core/types/core/Campus";
import FileExplorerEntry, {
    FileExplorerResponse,
    FileExplorerTypes,
} from "../core/types/system/FileExplorer";
import Dialog from "../core/components/dialog/Dialog";
import DialogContent from "../core/components/dialog/DialogContent";
import DialogFooter from "../core/components/dialog/DialogFooter";
import DialogCancel from "../core/components/dialog/DialogCancel";
import DialogOK from "../core/components/dialog/DialogOK";
import Panel from "../core/components/panels/Panel";
import DropDown from "../core/components/inputform/DropDown";
import { initialiseCampusDetails } from "../core/state/core/Campus";
import { Side } from "../core/types/system/Layout";
import AcademicTerm from "../core/types/core/AcademicTerm";
import { MockPromiseWrapper } from "../core/types/system/MockPromiseWrapper";
import { addGroup, initialiseGroupDetails } from "../core/state/archive/Groups";
import School from "../core/types/core/School";
import {
    importDOFiles,
    ImportParams,
    importRawFiles,
    importTDFiles,
} from "../core/state/files/Imports";
import Calendar from "../core/types/organiser/Calendar";
import { Timetable } from "../core/types/archive/Timetable";
import { initialiseOrganiserDetails } from "../core/state/organiser/Organisers";
import { initialiseTimetableDetails } from "../core/state/archive/Timetables";
import { openRecycled, RecycledType } from "../core/state/system/Recycled";
import Recycled from "../core/components/recycled/Recycled";

import "./FileExplorer.scss";

interface RouteFileExplorerProps {
    school: School;
    campuses: Campus[];
    groups: Group[];
    root?: Folder;
    path?: Folder[];
    contents?: File[];
    organisers: Calendar[];
    timetables: Timetable[];

    addAlert: (entry: AlertEntry) => Promise<AlertResult>;
    setPageLoading: (loading: boolean) => void;
    initialiseFolders: () => Promise<void>;
    initialiseCampusDetails: () => Promise<void>;
    initialiseGroupDetails: () => Promise<void>;
    initialiseOrganiserDetails: () => Promise<void>;
    initialiseTimetableDetails: () => Promise<void>;
    uploadFile: (parentId: number, files: BrowserFile[]) => Promise<void>;
    getFolderContents: (id: number) => Promise<void>;
    addGroup: (group: Group) => Promise<void>;
    addFileExplorer: (
        entry: FileExplorerEntry,
    ) => Promise<FileExplorerResponse>;
    addFolder: (name: string, parentId: number) => Promise<void>;
    moveFiles: (files: File[], target: number) => Promise<void>;
    copyFiles: (files: File[], target: number) => Promise<void>;
    renameFiles: (files: FileRename[]) => Promise<void>;
    downloadFile: (files: File[]) => Promise<void>;
    importTDFiles: (
        campusId: number,
        year: number,
        files: ImportParams[],
    ) => Promise<void>;
    importDOFiles: (
        campusId: number,
        year: number,
        files: ImportParams[],
    ) => Promise<void>;
    importRawFiles: (files: File[]) => Promise<void>;
    deleteFiles: (files: File[]) => Promise<void>;
    openRecycled: (types: RecycledType[]) => Promise<void>;
}

interface RouteFileExplorerState {
    loading: boolean;
    mode: "icons" | "list";
    sort: string[];
    dragging: boolean;
    selections: File[];
    importStep: number;
    importFiles: File[];
    importDates: Record<number, Date>;
    groupsOpen: boolean;
    connectionStatus: boolean;
    selectedGroup: Group;

    fileExplorerOpen: boolean;

    campusesPromise: MockPromiseWrapper<{
        campus: Campus;
        year: AcademicTerm;
    }>;
    campusesOpen: boolean;
    campusesCampus: Campus;
    campusesYear: AcademicTerm;

    dragOver: boolean;
}

class RouteFileExplorer extends React.Component<
    RouteFileExplorerProps,
    RouteFileExplorerState
> {
    state: RouteFileExplorerState = {
        loading: false,
        mode: "icons",
        sort: ["-isFolder", "name"],
        dragging: false,
        selections: [],
        importStep: 0,
        importFiles: [],
        importDates: {},
        groupsOpen: false,
        selectedGroup: null,
        connectionStatus: true,

        fileExplorerOpen: false,

        campusesOpen: false,
        campusesCampus: null,
        campusesYear: null,
        campusesPromise: null,

        dragOver: false,
    };

    async componentDidMount() {
        try {
            this.props.setPageLoading(true);
            await Promise.all([
                this.props.initialiseFolders(),
                this.props.initialiseCampusDetails(),
                this.props.initialiseGroupDetails(),
                this.props.initialiseOrganiserDetails(),
                this.props.initialiseTimetableDetails(),
            ]);
        } finally {
            this.props.setPageLoading(false);
        }
    }

    isSort(column: string) {
        return this.state.sort[1].replace("-", "") === column;
    }

    setSort(column: string) {
        if (this.isSort(column)) {
            this.setState({
                sort: [
                    this.state.sort[0],
                    this.state.sort[1].indexOf("-") === 0
                        ? this.state.sort[1].substring(1)
                        : "-" + this.state.sort[1],
                ],
            });
        } else {
            this.setState({
                sort: [this.state.sort[0], column],
            });
        }
    }

    selectItem(item: File) {
        const selected = this.isSelected(item);
        this.setState({
            selections: !selected
                ? [...this.state.selections, item]
                : this.state.selections.filter(
                      i => i.compositeId !== item.compositeId,
                  ),
        });
    }

    selectAll() {
        this.setState({
            selections: this.props.contents,
        });
    }

    deselectAll() {
        this.setState({
            selections: [],
        });
    }

    isSelected(item: File) {
        return this.state.selections.some(
            i => i.compositeId === item.compositeId,
        );
    }

    dblClick(item: File) {
        if (this.state.connectionStatus) {
            switch (item.fileType) {
                case SystemFileType.Folder:
                    this.setPath(item.id);
                    break;
            }
        }
    }

    setPath(id: number) {
        this.props.getFolderContents(id);

        this.setState({ selections: [] });
    }

    upPath() {
        if (this.props.path.length > 1)
            this.setPath(this.props.path[this.props.path.length - 2].id);
    }

    async getNewName(
        type: string,
        name: string,
        message?: string,
    ): Promise<string> {
        const result = await this.props.addAlert({
            type: AlertTypes.Prompt,
            icon: "add",
            styleColor: message ? "yellow" : null,
            title: `Add ${type} Name`,
            text: message,
            prompts: [
                {
                    type: "text",
                    display: `${type} Name`,
                    value: name,
                },
            ],
            okText: "Save",
        });

        if (result.response) {
            const newName = result.promptResults[0].value;

            if (this.props.contents.some(i => i.name === newName)) {
                return await this.getNewName(
                    "Folder",
                    name,
                    `A ${type} with this name already exists, please choose a different name.`,
                );
            }

            return newName;
        }
    }

    async addFolder() {
        const name = await this.getNewName("Folder", "New Folder");

        if (name) {
            try {
                this.props.setPageLoading(true);

                await this.props.addFolder(
                    name,
                    this.props.path[this.props.path.length - 1].id,
                );
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    async delete(context?: ContextMenuContextType) {
        const selections = this.getSelections(undefined, context);
        const result = await addAlert({
            type: AlertTypes.Confirm,
            icon: "delete_forever",
            styleColor: "red",
            title: "Delete Files",
            text: `Are you sure you want to delete these files?<br/><ul class="indented chevron"><li>${selections
                .map(item => item.name)
                .join("</li><li>")}</li></ul>`,
        });

        if (result.response) {
            try {
                this.props.setPageLoading(true);

                await this.props.deleteFiles(selections);

                this.setState({ selections: [] });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    async getTargetFolder() {
        const result = await this.props.addFileExplorer({
            type: FileExplorerTypes.Folders,
            title: "Move/Copy",
            denyUpload: true,
        });

        if (result.response) {
            if (
                this.state.selections.some(
                    file => file.id === result.selected.id,
                )
            ) {
                await this.props.addAlert({
                    type: AlertTypes.Alert,
                    icon: "error",
                    styleColor: "red",
                    title: "Invalid Target",
                    text: "You cannot move a folder into itself or one of its subfolders.",
                });
                return;
            }

            return result.selected;
        }

        return null;
    }

    getSelections(type?: SystemFileType, context?: ContextMenuContextType) {
        const contextItem = context
            ? this.props.contents.find(
                  i => i.compositeId === context.getValue(),
              )
            : null;
        const temp = contextItem
            ? [...this.state.selections, contextItem].distinct()
            : this.state.selections;
        return type ? temp.filter(i => i.fileType === type) : temp;
    }

    async move(context?: ContextMenuContextType) {
        const selections = this.getSelections(undefined, context);
        const result = await this.getTargetFolder();

        if (result) {
            try {
                this.props.setPageLoading(true);

                await this.props.moveFiles(selections, result.id);

                this.setState({ selections: [] });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    async copy(context?: ContextMenuContextType) {
        const selections = this.getSelections(undefined, context);
        const result = await this.getTargetFolder();

        if (result) {
            const names = await this.props.addAlert({
                type: AlertTypes.Prompt,
                icon: "edit",
                title: "Enter Copy Names",
                prompts: selections.map(item => {
                    const suffix = item.name.lastIndexOf(".");
                    return {
                        type: "text",
                        display: `Please enter the name for the copy of file ${item.name}`,
                        value:
                            suffix > 0
                                ? item.name.substring(0, suffix)
                                : item.name,
                        data: item,
                    };
                }),
            });

            if (names.response) {
                try {
                    this.props.setPageLoading(true);

                    await this.props.copyFiles(
                        names.promptResults.map(prompt => {
                            const suffixIndex =
                                prompt.data.name.lastIndexOf(".");
                            const suffix =
                                suffixIndex > 0
                                    ? prompt.data.name.substring(suffixIndex)
                                    : "";
                            return {
                                ...prompt.data,
                                name: `${prompt.value}${suffix}`,
                                fileName: `${prompt.value}${suffix}`,
                            };
                        }),
                        result.id,
                    );

                    this.setState({ selections: [] });
                } finally {
                    this.props.setPageLoading(false);
                }
            }
        }
    }

    async rename(context?: ContextMenuContextType) {
        const selections = this.getSelections(undefined, context);
        const names = await this.props.addAlert({
            type: AlertTypes.Prompt,
            icon: "edit",
            title: "Enter Copy Names",
            prompts: selections.map(item => {
                const suffix = item.name.lastIndexOf(".");
                return {
                    type: "text",
                    display: `Please enter the name for the copy of file ${item.name}`,
                    value:
                        suffix > 0 ? item.name.substring(0, suffix) : item.name,
                    data: item,
                };
            }),
        });

        if (names.response) {
            try {
                this.props.setPageLoading(true);

                await this.props.renameFiles(
                    names.promptResults.map(prompt => {
                        const suffixIndex = prompt.data.name.lastIndexOf(".");
                        const suffix =
                            suffixIndex > 0
                                ? prompt.data.name.substring(suffixIndex)
                                : "";
                        return {
                            file: prompt.data,
                            name: `${prompt.value}${suffix}`,
                        };
                    }),
                );

                this.setState({ selections: [] });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    async download(context?: ContextMenuContextType) {
        const selections = this.getSelections(undefined, context);

        try {
            this.props.setPageLoading(true);

            await this.props.downloadFile(selections);
        } finally {
            this.props.setPageLoading(false);
        }
    }

    dragOver(event: React.DragEvent<HTMLDivElement>) {
        event.preventDefault();
        this.setState({ dragOver: true });
    }

    dragOut(event: React.DragEvent<HTMLDivElement>) {
        event.preventDefault();
        this.setState({ dragOver: false });
    }

    async dragDrop(event: React.DragEvent<HTMLDivElement>) {
        event.preventDefault();

        const validFiles = await Promise.all(
            Array.from(event.dataTransfer.files)
                ?.filter(file => file.isTTSFile())
                .map(file => file.toBrowserFile()) ?? [],
        );

        if (validFiles.length) {
            this.props.uploadFile(
                this.props.path[this.props.path.length - 1].id,
                validFiles,
            );
        }

        this.setState({ dragOver: false });
    }

    async upload() {
        const selection = await Utility.uploadFile(
            ".sof9,.tdf9,.dof9,.ptf9,.sfx,.tfx,.zip",
        );

        if (selection?.length) {
            this.props.uploadFile(
                this.props.path[this.props.path.length - 1].id,
                selection,
            );
        }
    }

    async importPt(context?: ContextMenuContextType) {
        const selections = this.getSelections(
            SystemFileType.TimetableFile,
            context,
        );
        const campus = await this.selectCampus();

        const result = await this.props.addAlert({
            type: AlertTypes.Prompt,
            icon: "cloud_upload",
            title: "Enter Publish Dates",
            prompts: selections.map(item => ({
                type: "date",
                display: `Please enter the publish date for ${item.name}`,
                value: new Date(),
                minDate: new Date(campus.year.startDate),
                maxDate: new Date(campus.year.endDate),
            })),
        });

        if (result.response) {
            try {
                this.props.setPageLoading(true);

                await this.props.importTDFiles(
                    campus.campus.id,
                    campus.year.id,
                    selections.map((file, index) => ({
                        File: file,
                        StartDate: result.promptResults[index].value.toServer(),
                    })),
                );
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    async importDo(context?: ContextMenuContextType) {
        const selections = this.getSelections(
            SystemFileType.DailyOrganiserFile,
            context,
        );
        const campus = await this.selectCampus();
        const group = this.props.groups.find(
            g =>
                g.campusID === campus.campus.id &&
                g.termDatesID === campus.year.id,
        );

        if (
            this.props.organisers.some(item => item.archiveGroupID === group.id)
        ) {
            this.props.addAlert({
                type: AlertTypes.Alert,
                icon: "error",
                styleColor: "red",
                title: "Error",
                text:
                    "There is already a Daily Organiser linked to this " +
                    "Campus and Term/Semester.",
            });
            return;
        }

        if (!this.props.timetables.some(item => item.groupId === group.id)) {
            this.props.addAlert({
                type: AlertTypes.Alert,
                icon: "error",
                styleColor: "red",
                title: "Error",
                text:
                    "The selected group does not contain an appropriate " +
                    "published timetable. Please either publish a timetable " +
                    "to this group or select another group to use.",
            });
            return;
        }
        const startDate = campus.year.ranges.first().startDate.toDate();
        const endDate = campus.year.ranges.last().endDate.toDate();

        const result = await this.props.addAlert({
            type: AlertTypes.Confirm,
            icon: "warning",
            styleColor: "yellow",
            title: "Daily Organiser Date Range",
            text:
                "Importing this Daily Organiser to this " +
                "academic year will alter its start and end " +
                "dates to match, this will remove any data " +
                "outside of this range.<br/><br/>" +
                "The new dates will be " +
                startDate.toLocale() +
                " to " +
                endDate.toLocale() +
                ".",
            okText: "Proceed",
        });

        if (result.response) {
            try {
                this.props.setPageLoading(true);

                await this.props.importDOFiles(
                    campus.campus.id,
                    campus.year.id,
                    selections.map((file, index) => ({
                        File: file,
                        StartDate: startDate.toServer(),
                    })),
                );
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    async importRaw(context?: ContextMenuContextType) {
        const selections = [
            ...this.getSelections(SystemFileType.TimetableFile, context),
            ...this.getSelections(SystemFileType.StudentOptionsFile, context),
        ];

        try {
            this.props.setPageLoading(true);

            await this.props.importRawFiles(selections);
        } finally {
            this.props.setPageLoading(false);
        }
    }

    menuEvalDO() {
        const selections = this.getSelections(
            SystemFileType.DailyOrganiserFile,
        );
        return !selections.some(item => item.compositeId.startsWith("D"));
    }

    menuEvalTD() {
        const selections = this.getSelections(
            SystemFileType.DailyOrganiserFile,
        );
        return !selections.some(item => item.compositeId.startsWith("T"));
    }

    menuEvalTDSO() {
        const selections = this.getSelections(
            SystemFileType.DailyOrganiserFile,
        );
        return !selections.some(
            item =>
                item.compositeId.startsWith("T") ||
                item.compositeId.startsWith("S"),
        );
    }

    contextEval(context: ContextMenuContextType) {
        return (
            !Utility.notNullOrEmpty(context.getValue()) &&
            !this.state.selections.length
        );
    }

    contextEvalTD(context: ContextMenuContextType) {
        const value = context.getValue();
        return !Utility.notNullOrEmpty(value) || !value.startsWith("T");
    }

    contextEvalDO(context: ContextMenuContextType) {
        const value = context.getValue();
        return !Utility.notNullOrEmpty(value) || !value.startsWith("D");
    }

    contextEvalTDSO(context: ContextMenuContextType) {
        const value = context.getValue();
        return (
            !Utility.notNullOrEmpty(value) ||
            (!value.startsWith("T") && !value.startsWith("S"))
        );
    }

    contextEvalTDSODO(context: ContextMenuContextType) {
        const value = context.getValue();
        return (
            !Utility.notNullOrEmpty(value) ||
            (!value.startsWith("T") &&
                !value.startsWith("S") &&
                !value.startsWith("D"))
        );
    }

    selectCampus() {
        const id = Utility.newGuid().toString();
        const promise = new MockPromiseWrapper<{
            campus: Campus;
            year: AcademicTerm;
        }>(id, id, () => {
            this.setState({ campusesPromise: null });
        });
        this.setState({
            campusesOpen: true,
            campusesCampus: null,
            campusesYear: null,
            campusesPromise: promise,
        });
        return promise.promise;
    }

    canCompleteCampuses() {
        return !!this.state.campusesCampus && !!this.state.campusesYear;
    }

    cancelCampuses() {
        this.setState({
            campusesOpen: false,
            campusesCampus: null,
            campusesYear: null,
            campusesPromise: null,
        });
    }

    async completeCampuses() {
        if (this.state.campusesPromise) {
            let group = this.props.groups.find(
                g =>
                    g.campusID === this.state.campusesCampus.id &&
                    g.termDatesID === this.state.campusesYear.id,
            );

            if (!group) {
                try {
                    this.props.setPageLoading(true);
                    group = new Group({
                        schoolId: this.props.school.id,
                        campusID: this.state.campusesCampus.id,
                        termDatesID: this.state.campusesYear.id,
                        name: `${
                            this.state.campusesCampus.name
                        } - ${this.state.campusesYear.display.replace(
                            "/",
                            "_",
                        )}`,
                    });
                    await this.props.addGroup(group);
                } finally {
                    this.props.setPageLoading(false);
                }
            }

            this.state.campusesPromise.resolve({
                campus: this.state.campusesCampus,
                year: this.state.campusesYear,
            });
            this.setState({
                campusesOpen: false,
                campusesCampus: null,
                campusesYear: null,
                campusesPromise: null,
            });
        }
    }

    recycleBin() {
        this.props.openRecycled([RecycledType.Files]);
    }

    render() {
        return (
            <section
                className="full-height"
                onDragOver={this.dragOver.bind(this)}
                onDragLeave={this.dragOut.bind(this)}>
                <MainToolbarContents>
                    <ButtonMenu
                        icon="description"
                        text="Files"
                        tooltip="File management options">
                        <Button
                            icon="checklist_rtl"
                            text="Select All"
                            onClick={() => this.selectAll()}
                        />
                        <Button
                            icon="event_list"
                            text="Deselect All"
                            onClick={() => this.deselectAll()}
                        />
                        <Button
                            icon="add"
                            text="Add Folder"
                            onClick={() => this.addFolder()}
                        />
                        <Button
                            icon="folder"
                            text="Move Selected Items"
                            disabled={!this.state.selections.length}
                            onClick={() => this.move()}
                        />
                        <Button
                            icon="create_new_folder"
                            text="Copy Selected Items"
                            disabled={!this.state.selections.length}
                            onClick={() => this.copy()}
                        />
                        <Button
                            icon="edit"
                            text="Rename Selected Items"
                            disabled={!this.state.selections.length}
                            onClick={() => this.rename()}
                        />
                        <Button
                            icon="delete"
                            text="Delete Selected Items"
                            disabled={!this.state.selections.length}
                            onClick={() => this.delete()}
                        />
                    </ButtonMenu>
                    <ButtonMenu
                        icon="cloud_upload"
                        text="Import"
                        tooltip="File importing options">
                        <Button
                            icon="cloud_upload"
                            text="Import to Daily Organiser Web"
                            disabled={this.menuEvalDO()}
                            onClick={() => this.importDo()}
                        />
                        <Button
                            icon="cloud_upload"
                            text="Import to Published Timetables"
                            disabled={this.menuEvalTD()}
                            onClick={() => this.importPt()}
                        />
                        <Button
                            icon="cloud_upload"
                            text="Import Timetable/Student Options"
                            disabled={this.menuEvalTDSO()}
                            onClick={() => this.importRaw()}
                        />
                    </ButtonMenu>
                    <ToolbarButton
                        icon="file_upload"
                        text="Upload"
                        onClick={() => this.upload()}
                    />
                    <ToolbarButton
                        icon="file_download"
                        text="Download"
                        disabled={!this.state.selections.length}
                        onClick={() => this.download()}
                    />
                    <ToolbarButton
                        icon="delete"
                        text="Recycle Bin"
                        onClick={() => this.recycleBin()}
                    />

                    <InputGroup header="Display">
                        <RadioButton
                            label="Icons"
                            name="mode"
                            value="icons"
                            checked={this.state.mode === "icons"}
                            onChange={() => this.setState({ mode: "icons" })}
                        />
                        <RadioButton
                            label="List"
                            name="mode"
                            value="list"
                            checked={this.state.mode === "list"}
                            onChange={() => this.setState({ mode: "list" })}
                        />
                    </InputGroup>
                </MainToolbarContents>

                <div
                    className="flex-column full-height"
                    data-contextkey="main-file-explorer-box">
                    <div className="path-frame col-pane">
                        <div className="path-container">
                            <div>Path:</div>
                            <ul className="path-list">
                                {this.props.path?.map(item => (
                                    <li key={item.id}>
                                        <button
                                            disabled={
                                                !this.state.connectionStatus
                                            }
                                            onClick={() =>
                                                this.setPath(item.id)
                                            }>
                                            {item.name.toLowerCase() === "root"
                                                ? "TDS"
                                                : item.name}
                                            /
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            <button
                                className="path-button"
                                disabled={!this.state.connectionStatus}
                                onClick={() => this.upPath()}
                                data-tip="Navigate up one folder">
                                <i>arrow_upward</i>
                            </button>
                        </div>
                    </div>

                    <div
                        className={`list-frame col file-list-frame ${
                            this.state.mode === "icons"
                                ? "view-tiles"
                                : "view-list"
                        }`}>
                        <div className="list-headers">
                            <span className="list-headers-icon"></span>
                            <span
                                className="list-headers-name"
                                onClick={() => this.setSort("name")}>
                                Name
                                {this.isSort("name") && (
                                    <i>
                                        {this.state.sort[1].indexOf("-") === 0
                                            ? "expand_more"
                                            : "expand_less"}
                                    </i>
                                )}
                            </span>
                            <span
                                className="list-headers-type"
                                onClick={() => this.setSort("typeDisplay")}>
                                Type
                                {this.isSort("typeDisplay") && (
                                    <i>
                                        {this.state.sort[1].indexOf("-") === 0
                                            ? "expand_more"
                                            : "expand_less"}
                                    </i>
                                )}
                            </span>
                            <span
                                className="list-headers-date"
                                onClick={() => this.setSort("editedOn")}>
                                Date
                                {this.isSort("editedOn") && (
                                    <i>
                                        {this.state.sort[1].indexOf("-") === 0
                                            ? "expand_more"
                                            : "expand_less"}
                                    </i>
                                )}
                            </span>
                        </div>

                        {this.props.contents
                            ?.slice()
                            .sort(Utility.getSorter(this.state.sort))
                            .map((item, index) => (
                                <div
                                    key={index}
                                    className="list-item"
                                    onClick={() => this.selectItem(item)}
                                    onDoubleClick={() => this.dblClick(item)}
                                    data-contextkey="main-file-explorer"
                                    data-contextvalue={item.compositeId}>
                                    <div
                                        className={`${
                                            item.isFolder
                                                ? "folder-item-container"
                                                : "file-item-container"
                                        }${
                                            this.isSelected(item)
                                                ? " selected"
                                                : ""
                                        }`}>
                                        <div
                                            className="file-item-subtitle"
                                            style={{ cursor: "default" }}>
                                            <span style={{ cursor: "default" }}>
                                                <span>Uploaded - </span>
                                                <span>
                                                    {item.editedOn.toLocaleString()}
                                                </span>
                                            </span>
                                            <span
                                                className="file-details"
                                                style={{ cursor: "default" }}>
                                                Version
                                                {item.typeDisplay}
                                            </span>
                                        </div>
                                        <div
                                            className={`${
                                                item.isFolder
                                                    ? "folder-item-title"
                                                    : "file-item-title"
                                            } ${
                                                item.fileType ===
                                                SystemFileType.TimetableFile
                                                    ? "td-file"
                                                    : item.fileType ===
                                                      SystemFileType.DailyOrganiserFile
                                                    ? "do-file"
                                                    : item.fileType ===
                                                      SystemFileType.StudentOptionsFile
                                                    ? "so-file"
                                                    : ""
                                            }`}
                                            style={{ cursor: "default" }}>
                                            <span className="file-title-head">
                                                <span className="file-icon1"></span>
                                                <span className="file-icon2"></span>
                                                <label
                                                    className="checkbox file-item-select"
                                                    onClick={e =>
                                                        e.stopPropagation()
                                                    }>
                                                    <input
                                                        type="checkbox"
                                                        checked={this.isSelected(
                                                            item,
                                                        )}
                                                        onChange={() =>
                                                            this.selectItem(
                                                                item,
                                                            )
                                                        }
                                                    />
                                                    <span className="checkmark"></span>
                                                </label>
                                                <span className="file-text">
                                                    {item.badge}
                                                </span>
                                            </span>
                                            {item.isFolder ? (
                                                <span
                                                    className="folder-link"
                                                    onClick={event => {
                                                        this.setPath(item.id);
                                                        event.stopPropagation();
                                                    }}
                                                    tabIndex={0}>
                                                    <span>{item.name}</span>
                                                </span>
                                            ) : (
                                                <span
                                                    className="file-name"
                                                    style={{
                                                        cursor: "default",
                                                    }}
                                                    ng-if="!actions.isFolder(item)">
                                                    <span>{item.name}</span>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}

                        {!this.props.contents?.length && (
                            <div className="no-details">
                                <span>No files or folders</span>
                            </div>
                        )}
                    </div>
                </div>

                <div
                    className={`explorer-file-drop-frame${
                        this.state.dragOver ? " drag" : ""
                    }`}
                    onDrop={this.dragDrop.bind(this)}>
                    <span>Drop Files Here To Upload</span>
                </div>

                <Dialog
                    dialogIcon="checklist"
                    dialogTitle="Select Campus and Academic Year"
                    showDialog={this.state.campusesOpen}
                    canResize={false}
                    width={360}>
                    <DialogContent>
                        <Panel title="Campus">
                            <DropDown
                                noType={true}
                                fullWidth={true}
                                value={this.state.campusesCampus}
                                options={this.props.campuses.map(c => ({
                                    value: c,
                                    text: c.name,
                                }))}
                                onChange={value =>
                                    this.setState({
                                        campusesCampus: value,
                                    })
                                }
                            />
                        </Panel>
                        {!!this.state.campusesCampus && (
                            <Panel
                                title="Academic Year"
                                clearanceSide={Side.Top}>
                                <DropDown
                                    noType={true}
                                    fullWidth={true}
                                    value={this.state.campusesYear}
                                    options={this.state.campusesCampus.terms.map(
                                        c => ({
                                            value: c,
                                            text: c.display,
                                        }),
                                    )}
                                    onChange={value =>
                                        this.setState({
                                            campusesYear: value,
                                        })
                                    }
                                />
                            </Panel>
                        )}
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel
                            onClick={this.cancelCampuses.bind(this)}
                        />
                        <DialogOK
                            disabled={!this.canCompleteCampuses()}
                            onClick={this.completeCampuses.bind(this)}
                        />
                    </DialogFooter>
                </Dialog>

                <Recycled />

                <ContextFragment name="main-file-explorer">
                    <ContextMenuItem
                        id="main_explorer_menu_spacer_files"
                        separator={true}
                        contextKey="main-file-explorer"
                        order={0}
                    />
                    <ContextMenuItem
                        id="main_explorer_move"
                        icon="folder"
                        name="Move"
                        contextKey="main-file-explorer"
                        handler={(e, c) => this.move(c)}
                        disabledHandler={this.contextEval.bind(this)}
                        order={1}
                    />
                    <ContextMenuItem
                        id="main_explorer_copy"
                        icon="create_new_folder"
                        name="Copy"
                        contextKey="main-file-explorer"
                        handler={(e, c) => this.copy(c)}
                        disabledHandler={this.contextEval.bind(this)}
                        order={2}
                    />
                    <ContextMenuItem
                        id="main_explorer_rename"
                        icon="edit"
                        name="Rename"
                        contextKey="main-file-explorer"
                        handler={(e, c) => this.rename(c)}
                        disabledHandler={this.contextEval.bind(this)}
                        order={3}
                    />
                    <ContextMenuItem
                        id="main_explorer_delete"
                        icon="delete"
                        name="Delete"
                        contextKey="main-file-explorer"
                        handler={(e, c) => this.delete(c)}
                        disabledHandler={this.contextEval.bind(this)}
                        order={4}
                    />
                    <ContextMenuItem
                        id="main_explorer_download"
                        icon="download"
                        name="Download"
                        contextKey="main-file-explorer"
                        handler={(e, c) => this.download(c)}
                        hideHandler={this.contextEvalTDSODO.bind(this)}
                        order={5}
                    />
                    <ContextMenuItem
                        id="main_explorer_menu_spacer_files_import"
                        separator={true}
                        contextKey="main-file-explorer"
                        hideHandler={this.contextEvalTDSODO.bind(this)}
                        order={6}
                    />
                    <ContextMenuItem
                        id="main_explorer_import_pt"
                        icon="cloud_upload"
                        name="Import to Published Timetables"
                        contextKey="main-file-explorer"
                        handler={(e, c) => this.importPt(c)}
                        hideHandler={this.contextEvalTD.bind(this)}
                        order={7}
                    />
                    <ContextMenuItem
                        id="main_explorer_import_do"
                        icon="cloud_upload"
                        name="Import to Daily Organiser Web"
                        contextKey="main-file-explorer"
                        handler={(e, c) => this.importDo(c)}
                        hideHandler={this.contextEvalDO.bind(this)}
                        order={8}
                    />
                    <ContextMenuItem
                        id="main_explorer_import_rw"
                        icon="cloud_upload"
                        name="Import Timetable/Student Options"
                        contextKey="main-file-explorer"
                        handler={(e, c) => this.importRaw(c)}
                        hideHandler={this.contextEvalTDSO.bind(this)}
                        order={9}
                    />
                    <ContextMenuItem
                        id="main_explorer_menu_spacer_files_box"
                        separator={true}
                        contextKey="main-file-explorer-box"
                        order={0}
                    />
                    <ContextMenuItem
                        id="main_explorer_select_all"
                        icon="checklist_rtl"
                        name="Select All"
                        contextKey="main-file-explorer-box"
                        handler={this.selectAll.bind(this)}
                        order={1}
                    />
                    <ContextMenuItem
                        id="main_explorer_deselect_all"
                        icon="checklist_rtl"
                        name="Deselect All"
                        contextKey="main-file-explorer-box"
                        handler={this.deselectAll.bind(this)}
                        order={2}
                    />
                    <ContextMenuItem
                        id="main_explorer_add_folder"
                        icon="add"
                        name="Add Folder"
                        contextKey="main-file-explorer-box"
                        handler={this.addFolder.bind(this)}
                        order={3}
                    />
                </ContextFragment>
            </section>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { school, campus, folders, groups, organisers, timetable } = state.db;
    return {
        school: school.school,
        campuses: campus.campuses,
        root: folders.root,
        path: folders.path,
        contents: folders.viewed,
        groups: groups.groups,
        timetables: timetable.timetables,
        organisers: organisers.organisers,
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return {
        addAlert: (entry: AlertEntry) => addAlert(entry),
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        initialiseFolders: () => unwrappedDispatch(initialiseFolders()),
        initialiseCampusDetails: () =>
            unwrappedDispatch(initialiseCampusDetails()),
        initialiseGroupDetails: () =>
            unwrappedDispatch(initialiseGroupDetails()),
        initialiseOrganiserDetails: () => initialiseOrganiserDetails(),
        initialiseTimetableDetails: () => initialiseTimetableDetails(),
        getFolderContents: (id: number) => getFolderContents(id),
        uploadFile: (parentId: number, files: BrowserFile[]) =>
            uploadFile(parentId, files),
        addGroup: (group: Group) => unwrappedDispatch(addGroup(group)),
        addFileExplorer: (
            entry: FileExplorerEntry,
        ): Promise<FileExplorerResponse> =>
            unwrappedDispatch(addFileExplorer(entry)),
        addFolder: (name: string, parentId: number) =>
            addFolder(name, parentId),
        moveFiles: (files: File[], target: number) =>
            unwrappedDispatch(
                moveFiles({
                    files,
                    target,
                }),
            ),
        copyFiles: (files: File[], target: number) =>
            unwrappedDispatch(
                copyFiles({
                    files,
                    target,
                }),
            ),
        renameFiles: (files: FileRename[]) => renameFiles(files),
        downloadFile: (files: File[]) => downloadFile(files),
        importTDFiles: (
            campusId: number,
            year: number,
            files: ImportParams[],
        ) => importTDFiles(campusId, year, files),
        importDOFiles: (
            campusId: number,
            year: number,
            files: ImportParams[],
        ) => importDOFiles(campusId, year, files),
        importRawFiles: (files: File[]) => importRawFiles(files),
        deleteFiles: (files: File[]) => deleteFiles(files),
        openRecycled: (types: RecycledType[]) => openRecycled(types),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteFileExplorer);
